import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, GripVertical, Save, ListChecks } from 'lucide-react';
import { invalidateJobStatusCache } from '@/hooks/useJobStatusConfig';
import { STEPPER_COLORS } from '@/components/shared/StatusStepper';

/**
 * EstimateStatusSettings — Settings > Estimate Status (Fix B, 2026-08-21).
 * Alian: "esto yo lo puedo ajustar, igual que el de nosotros [Job Status].
 * Pero me hace falta que me salga acá arriba" + wants a color per status.
 * Unlike Job Status, the set of statuses here is FIXED — renaming/deleting
 * the internal `key` would break estimates already saved with that value,
 * so only label, color and order are editable (no add/delete controls).
 */
export default function EstimateStatusSettings() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [draggedIdx, setDraggedIdx] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/job-status/estimate');
      setItems(res.data || []);
    } catch {
      toast.error('No se pudo cargar Estimate Status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleRename = (idx, label) => {
    setItems(prev => prev.map((s, i) => (i === idx ? { ...s, label } : s)));
  };

  const handleColorChange = (idx, color) => {
    setItems(prev => prev.map((s, i) => (i === idx ? { ...s, color } : s)));
  };

  const handleDragStart = (idx) => setDraggedIdx(idx);
  const handleDragOver = (e) => e.preventDefault();
  const handleDrop = (idx) => {
    if (draggedIdx === null || draggedIdx === idx) return;
    const next = [...items];
    const [moved] = next.splice(draggedIdx, 1);
    next.splice(idx, 0, moved);
    setItems(next);
    setDraggedIdx(null);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.put('/job-status/estimate', { statuses: items });
      setItems(res.data || []);
      invalidateJobStatusCache();
      toast.success('Cambios guardados');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo guardar');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;
  }

  return (
    <Card className="bg-white border-gray-100" data-testid="estimate-status-settings">
      <CardContent className="p-6">
        <div className="mb-6">
          <p className="text-sm font-semibold text-gray-700 flex items-center gap-2"><ListChecks className="w-4 h-4 text-[#3399FF]" /> Estimate Status</p>
          <p className="text-xs text-gray-400 mt-0.5">
            Renombre, recoloree o reordene (arrastrar) los estados del estimado. La clave interna
            no se puede cambiar ni borrar — así los estimados ya guardados no se rompen.
          </p>
        </div>

        <div className="divide-y divide-gray-100 border border-gray-100 rounded-lg" data-testid="estimate-status-list">
          {items.map((s, idx) => (
            <div
              key={s.key}
              draggable
              onDragStart={() => handleDragStart(idx)}
              onDragOver={handleDragOver}
              onDrop={() => handleDrop(idx)}
              className={`flex items-center gap-2 px-3 py-2 ${draggedIdx === idx ? 'opacity-40' : ''}`}
              data-testid={`estimate-status-row-${s.key}`}
            >
              <GripVertical className="w-4 h-4 text-gray-300 cursor-grab flex-shrink-0" />
              <Input
                value={s.label}
                onChange={e => handleRename(idx, e.target.value)}
                className="h-8 text-sm flex-1"
                data-testid={`estimate-status-label-${s.key}`}
              />
              <span className="text-[10px] text-gray-400 font-mono flex-shrink-0 w-32 truncate" title={s.key}>{s.key}</span>
              <Select value={s.color || 'gray'} onValueChange={v => handleColorChange(idx, v)}>
                <SelectTrigger className="h-8 w-28 text-xs flex-shrink-0" data-testid={`estimate-status-color-${s.key}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STEPPER_COLORS.map(c => (
                    <SelectItem key={c.key} value={c.key}>
                      <span className="flex items-center gap-1.5"><span className={`w-2.5 h-2.5 rounded-full ${c.swatch}`} />{c.label}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ))}
        </div>

        <div className="flex justify-end mt-4">
          <Button className="bg-[#003366] hover:bg-[#004080] text-white" onClick={handleSave} disabled={saving} data-testid="estimate-status-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Save className="w-4 h-4 mr-1.5" />} Guardar cambios
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
